import type { CSSProperties } from 'react';
import { Lien } from './navigation.js';
import type { OngletDossier } from './onglets-dossier.js';
import { CIBLE_MIN, COULEURS, IDENTITE } from './styles.js';

/**
 * Barre d'onglets du dossier ouvert : un vrai lien par onglet, le dossier courant conservé, l'onglet actuel marqué.
 * Sur mobile (`defilant`, juge du 30/10, U7), les onglets tiennent sur une ligne qui défile horizontalement : quatorze onglets
 * occupaient six lignes et poussaient le contenu de l'écran sous la ligne de flottaison.
 */
export function BarreOngletsDossier({
  onglets,
  courant,
  defilant = false,
}: {
  onglets: readonly OngletDossier[];
  courant: string | null;
  defilant?: boolean;
}) {
  return (
    <nav
      aria-label="Sections du dossier"
      data-onglets-dossier=""
      style={{
        display: 'flex',
        ...(defilant ? { flexWrap: 'nowrap', overflowX: 'auto' } : { flexWrap: 'wrap' }),
        gap: 4,
        borderBottom: '1px solid #E2E1EA',
        marginBottom: 16,
        fontSize: 14,
      }}
    >
      {onglets.map((o) => {
        const actuel = o.cle === courant;
        return (
          <Lien
            key={o.cle}
            vers={o.chemin}
            courant={actuel}
            enfants={o.libelle}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: CIBLE_MIN,
              padding: '0 14px',
              boxSizing: 'border-box',
              color: COULEURS.marine,
              textDecoration: 'none',
              fontWeight: actuel ? 600 : 400,
              borderBottom: `3px solid ${actuel ? IDENTITE.orange : 'transparent'}`,
              ...(defilant ? { whiteSpace: 'nowrap', flexShrink: 0 } : {}),
            }}
          />
        );
      })}
    </nav>
  );
}

/** Écran du dossier ouvert sans dossier désigné : la liste des dossiers et « Nouveau dossier » sont à un clic. */
export function RenvoiListeDossiers({ liste, nouveau }: { liste: string; nouveau: string }) {
  const lien: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: CIBLE_MIN,
    padding: '0 16px',
    boxSizing: 'border-box',
    borderRadius: 8,
    textDecoration: 'none',
  };
  return (
    <aside
      data-renvoi="liste-dossiers"
      aria-label="Aucun dossier ouvert"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        marginBottom: 16,
        background: COULEURS.blanc,
        border: '1px solid #E2E1EA',
        borderRadius: 12,
      }}
    >
      <span style={{ flex: '1 1 260px' }}>Aucun dossier n’est ouvert : choisissez-en un dans la liste des dossiers, ou créez-en un.</span>
      <Lien
        vers={liste}
        enfants="Voir la liste des dossiers"
        style={{ ...lien, border: `1px solid ${COULEURS.marine}`, color: COULEURS.marine }}
      />
      <Lien
        vers={nouveau}
        enfants="Nouveau dossier"
        style={{ ...lien, background: COULEURS.boutonPrincipal, color: COULEURS.blanc, fontWeight: 600 }}
      />
    </aside>
  );
}
